"use client";

/**
 * Detect an active supplier cookie session while browsing customer pages.
 * Used to show "Supplier Dashboard" instead of forcing re-login after Back/Home.
 *
 * Behaviour:
 * 1. hasLocalSupplierHint() — checks localStorage for any known supplier key
 *    with a real (non-placeholder) value. If absent → no network call at all.
 * 2. Module-level pendingFetch dedup — HeaderDesktop, MobileSidebar and
 *    SettingsModal all mount simultaneously; only the first one fires a fetch,
 *    the other two attach to the same Promise. Saves 2 redundant /me requests.
 * 3. pendingFetch resets to null after resolution so a fresh check can be
 *    made on the next page navigation (full remount).
 */
import { useEffect, useState } from "react";
import { config } from "@/config";
import {
  LIVE_PUBLIC_API_V1,
  resolveSafeBrowserApiV1Base,
} from "@/config/apiBase";

// ─── Module-level dedup ──────────────────────────────────────────────────────
/** Shared in-flight promise — reused by all mounted hook instances. */
let pendingFetch = null;

// ─── Helpers ─────────────────────────────────────────────────────────────────
function supplierMeUrl() {
  const raw = String(
    config.SUPPLIER_API_BASE_URL || config.API_BASE_URL || "",
  ).trim();
  const host =
    typeof window !== "undefined" ? window.location.hostname : "";
  const base =
    resolveSafeBrowserApiV1Base(raw, host) || LIVE_PUBLIC_API_V1;
  return `${base}/supplier/auth/me`;
}

/**
 * Returns true only when localStorage contains at least one supplier key with
 * a real (non-placeholder) string value. Rejects "null", "undefined", and "".
 * Inspired by the customer AuthContext bug where literal "null" strings were
 * treated as truthy hints.
 */
function hasLocalSupplierHint() {
  if (typeof window === "undefined") return false;
  try {
    const keysToCheck = ["supplier_id", "supplier_onboarding_status", "tripsadora_mode"];
    for (const key of keysToCheck) {
      const val = localStorage.getItem(key);
      if (
        val !== null &&
        val !== "null" &&
        val !== "undefined" &&
        val.trim() !== ""
      ) {
        // tripsadora_mode must equal "supplier" specifically
        if (key === "tripsadora_mode" && val !== "supplier") continue;
        return true;
      }
    }
  } catch (_) {}
  return false;
}

// ─── Exported helpers ─────────────────────────────────────────────────────────
export function goToSupplierDashboard(router) {
  try {
    localStorage.setItem("tripsadora_mode", "supplier");
  } catch (_) {}
  if (router?.push) router.push("/supplier/dashboard");
  else if (typeof window !== "undefined") {
    window.location.assign("/supplier/dashboard");
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useSupplierSessionHint() {
  const [hasSupplierSession, setHasSupplierSession] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;

    // Guard — skip network entirely if no local hint
    if (!hasLocalSupplierHint()) {
      setHasSupplierSession(false);
      setChecking(false);
      return;
    }

    // Dedup — reuse in-flight fetch if a sibling component already started it
    if (!pendingFetch) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      pendingFetch = fetch(supplierMeUrl(), {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      })
        .then((res) => res.ok)
        .catch(() => false)
        .finally(() => {
          clearTimeout(timeout);
          // Reset so next full page load re-checks freshly
          pendingFetch = null;
        });
    }

    pendingFetch.then((ok) => {
      if (!cancelled) {
        setHasSupplierSession(ok);
        setChecking(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return { hasSupplierSession, checking };
}
